import type { Action } from 'svelte/action';

export interface ClickOutsideAttributes {
  'on:clickoutside'?: (event: CustomEvent<MouseEvent>) => void;
}

/**
 * Limit to which the click event will trigger `clickoutside`
 * @public
 *
 * @remarks
 * Currently only the parent option is supported
 */
export interface ClickOutsideLimit {
  /** Click event beyond the `boundingRect` of this parent node will not trigger `clickoutside` */
  parent: HTMLElement;
}

/**
 * svelte action parameters to config behavior of `clickoutside`
 * @public
 */
export interface ClickOutsideParameters {
  /** whether to activate the action. Default to `true` */
  enabled: boolean;
  /** limit to which the click event will trigger `clickoutside` */
  limit?: ClickOutsideLimit;
  /** event to register callback. Default to `click` */
  event?: string;
  /** options to add to `addEventListener` */
  options?: AddEventListenerOptions | boolean;
}

export const clickoutside: Action<
  HTMLElement,
  Partial<ClickOutsideParameters>,
  ClickOutsideAttributes
> = function (node, parameters = { enabled: true }) {
  let { enabled, eventType, nodeForEvent, options, capture } =
    resolveParameters(parameters);
  const handle = (event: Event) => {
    if (
      node &&
      !node.contains(event.target as Node) &&
      !event.defaultPrevented
    ) {
      node.dispatchEvent(new CustomEvent('clickoutside', { detail: event }));
    }
  };

  if (parameters.enabled !== false) {
    nodeForEvent.addEventListener(eventType, handle, options);
  }

  return {
    update(update) {
      nodeForEvent.removeEventListener(eventType, handle, capture);
      ({ enabled, eventType, nodeForEvent, options, capture } =
        resolveParameters(update));
      if (enabled) nodeForEvent.addEventListener(eventType, handle, options);
    },
    destroy() {
      nodeForEvent.removeEventListener(eventType, handle, capture);
    }
  };
};

/** @internal */
export function resolveParameters(parameters: Partial<ClickOutsideParameters>) {
  return {
    enabled: parameters.enabled ?? true,
    nodeForEvent: parameters.limit?.parent ?? document,
    eventType: parameters.event ?? 'click',
    options: parameters.options,
    capture:
      typeof parameters.options === 'object'
        ? parameters.options?.capture
        : parameters.options
  };
}
